// 资源标注功能集合 (包含点、线、面绘制)

// 资源标注流程说明
// 1. map：添加drawResourceLayer：放置绘制的要素，bufferResourceLayer：放置要素的缓冲区
// 2. startResourceDraw 开始点线面绘制，action = this.drawResource.create(type), 监听事件，响应createPointGraphic/createPolylineGraphic/createPolygonGraphic，绘制在drawResourceLayer
// 3. 当在地图双击结束绘制，即监听到 'draw-complete'， 开始生成缓冲区 => this.drawCompleteEvent(graphic)
// 4. geometryService.buffer后得到缓冲区并绘制在bufferResourceLayer
// 5. 两种方案判断哪些点在缓冲区内
// 5.1 (1)点位不多的，可以直接循环判断是否在缓冲区内,geometryEngine.contains(缓冲区,点)，true: 在范围内  false: 在范围外
// 5.2 (2)把绘制的graphic.geometry的点集传到后台，sql判断，类似宜昌楼栋查询，（mysql楼栋表发布服务及查询20210129（请勿外传）.docx）

// 参考
// https://developers.arcgis.com/javascript/latest/sample-code/sandbox/index.html?sample=draw-line
// https://developers.arcgis.com/javascript/latest/sample-code/sandbox/index.html?sample=sketch-geometries
// https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-SimpleMarkerSymbol.html
// https://developers.arcgis.com/javascript/latest/api-reference/esri-views-draw-DrawAction.html

export default {
  data () {
    return {
      drawModules: [
        'esri/tasks/GeometryService', // 几何服务
        'esri/views/draw/Draw', // 绘制资源时使用
        'esri/geometry/Circle', // 绘制圆形范围时使用
        'esri/geometry/geometryEngine', // 绘制资源时使用
        'esri/tasks/support/BufferParameters'
      ],
      drawResourceLayer: null,
      drawResource: null,
      bufferResourceLayer: null,
      geometryService: null,
      completeGeometry: null, // 绘画完成结果
      rangeMeter: 10000
    }
  },
  methods: {
    // 资源标注初始化
    createResourceDraw () {
      // 几何服务
      this.geometryService = new this.gisConstructor.GeometryService(this.gisConfig.geometryService)
      // 绘制图层
      this.drawResource = new this.gisConstructor.Draw({ view: this.view })
      this.drawResourceLayer = new this.gisConstructor.GraphicsLayer()
      this.map.add(this.drawResourceLayer)
      // 缓冲区图层
      this.bufferResourceLayer = new this.gisConstructor.GraphicsLayer()
      this.map.add(this.bufferResourceLayer)
      this.view.ui.remove('attribution')
    },
    // 开始画点
    startResourceDraw (type) {
      this.view.focus()
      this.drawResource.reset()
      let action
      switch (type) {
        case 'point': { // 点
          action = this.drawResource.create('point')
          action.on(['draw-complete'], evt => { this.createPointGraphic(evt, 'draw-complete') })
          break
        }
        case 'polyline': { // 线
          action = this.drawResource.create('polyline')
          action.on(['vertex-add', 'vertex-remove', 'cursor-update', 'redo', 'undo'], evt => { this.createPolylineGraphic(evt, '') })
          action.on(['draw-complete'], evt => { this.createPolylineGraphic(evt, 'draw-complete') })
          break
        }
        case 'polygon': { // 面
          action = this.drawResource.create('polygon')
          action.on(['vertex-add', 'vertex-remove', 'cursor-update', 'redo', 'undo'], evt => { this.createPolygonGraphic(evt, '') })
          action.on(['draw-complete'], evt => { this.createPolygonGraphic(evt, 'draw-complete') })
          break
        }
      }
    },
    // 画点处理
    createPointGraphic (event, type) {
      const vertices = event.vertices[0]
      if (!vertices || vertices.length < 1) return console.error('未获取到有效点位', event)
      this.clearResourceDraw()
      const graphic = new this.gisConstructor.Graphic({
        geometry: {
          type: 'point',
          x: vertices[0],
          y: vertices[1],
          spatialReference: this.view.spatialReference
        },
        symbol: { type: 'simple-marker', style: 'circle', color: '#ff7403', size: '8px' }
      })
      this.drawResourceLayer.add(graphic)
      if (type === 'draw-complete') this.drawCompleteEvent(graphic)
    },
    // 画线处理
    createPolylineGraphic (event, type) {
      const vertices = event.vertices
      this.clearResourceDraw()
      const graphic = new this.gisConstructor.Graphic({
        geometry: {
          type: 'polyline',
          paths: vertices,
          spatialReference: this.view.spatialReference
        },
        symbol: {
          type: 'simple-line',
          color: '#ff7403',
          width: 2,
          cap: 'round',
          join: 'round'
        }
      })
      this.drawResourceLayer.add(graphic)
      if (type === 'draw-complete') this.drawCompleteEvent(graphic)
    },
    // 画面处理
    createPolygonGraphic (event, type) {
      const vertices = event.vertices
      this.clearResourceDraw()
      let graphic = new this.gisConstructor.Graphic({
        geometry: {
          type: 'polygon',
          rings: vertices,
          spatialReference: this.view.spatialReference
        },
        symbol: {
          type: 'simple-fill', // autocasts as SimpleFillSymbol
          color: [3, 255, 240, 0.1],
          style: 'solid',
          outline: {
            color: [255, 116, 3],
            width: 2
          }
        }
      })
      this.drawResourceLayer.add(graphic)
      if (type === 'draw-complete') this.drawCompleteEvent(graphic)
    },
    // 绘画完成统一处理事件
    drawCompleteEvent (graphic) {
      this.completeGeometry = graphic.geometry
      this.storageGeometryPoint()
    },
    // 解决方案1：点位不多的，可以直接循环判断是否在缓冲区内,缓存区查询点位 node: 需要查询是否在范围内的坐标
    async queryBufferGeometry (node = {}) {
      if (!this.completeGeometry) return console.error('[startResourceDraw]未执行绘画函数,请先绘画')
      const geometry = this.completeGeometry
      let x = node.x || node.longitude
      let y = node.y || node.latitude
      if (!x || !y) return console.warn('[queryBufferGeometry]当前查询参数无效')
      const bufferParameters = new this.gisConstructor.BufferParameters({
        geometries: [geometry],
        distances: [this.rangeMeter],
        unit: 'meters',
        geodesic: true,
        bufferSpatialReference: this.view.spatialReference,
        outSpatialReference: this.view.spatialReference
      })
      const sfillSymbol = {
        type: 'simple-fill',
        style: 'solid',
        outline: { color: 'white', width: 1 }
      }
      const results = await this.geometryService.buffer(bufferParameters)
      // 缓冲得到的geometry:results
      if (results.length > 0) {
        // drawResourceLayer.graphics.removeAll()
        this.bufferResourceLayer.add(new this.gisConstructor.Graphic({ geometry: results[0], symbol: sfillSymbol }))
        // 要查询的点位信息
        let insideGeometry = new this.gisConstructor.Point(x, y, this.view.spatialReference)
        // 判断一个点是否在缓冲区内
        let include = this.gisConstructor.geometryEngine.contains(results[0], insideGeometry)
        console.log(include)
        return {
          x, y, include // true: 在范围内  false: 在范围外
        }
      }
      return { x, y, include: false }
    },
    // 解决方案2：geometry的点集传到后台， 把xy点传给后台sql查询
    storageGeometryPoint () {
      let data = {}
      let pointsStr = ''
      let polygonFirst = '' // 画面时需要记录第一个点位置拼接到最后一个点位置完成闭合
      const geometry = this.completeGeometry
      let points, point, x, y
      let pointArr = []
      let type = geometry.type
      switch (type) {
        case 'point': {
          point = geometry
          x = point.x
          y = point.y
          pointArr.push({ x, y, longitude: x, latitude: y })
          pointsStr = `,${x} ${y}`
          break
        }
        case 'polyline': {
          points = geometry.paths[0]
          for (const i in points) {
            point = points[i]
            x = point[0]
            y = point[1]
            pointArr.push({ x, y, longitude: x, latitude: y })
            pointsStr += `,${x} ${y}`
          }
          break
        }
        case 'polygon': {
          points = geometry.rings[0]
          for (const i in points) {
            point = points[i]
            x = point[0]
            y = point[1]
            pointArr.push({ x, y, longitude: x, latitude: y })
            pointsStr += `,${x} ${y}`
            // eslint-disable-next-line eqeqeq
            if (i == 0) polygonFirst = `,${x} ${y}`
          }
          break
        }
      }
      data = { points: pointArr, code: this.buildCompleteCode(), pointsStr: pointsStr.substr(1) + polygonFirst }
      this.$emit('draw-complete', data)
      return data
      // xy点集传到后台（mysql楼栋表发布服务及查询20210129（请勿外传）.docx）
      // 空间查询sql，缓冲半径100
      // 点 select * from yygrid_poly where ST_Intersects(ST_Buffer(st_geomFromText('POINT(122.99802687647922 46.70226585088523)',0),100/111195),shape)
      // 线 select * from yygrid_poly where ST_Intersects(ST_Buffer(st_geomFromText('LINESTRING(122.99802687647922 46.70226585088523,123.01038648376296 46.59377596472799)',0),100/111195),shape)
      // 面 select * from yygrid_poly where ST_Intersects(ST_Buffer(st_geomFromText('POLYGON((122.95820147523163 46.791529681267775,122.99802687647922 46.70226585088523,123.01038648376296 46.59377596472799,
      // 123.00901319406476 46.59377596472799,123.00763990436657 46.59377596472799,123.00489332497018 46.59377596472799,123.0021467455738 46.59377596472799,122.99802687647922 46.59377596472799,
      // 122.99390700738465 46.59514925442618,122.8867904109256 46.6706801878268, 122.95820147523163 46.791529681267775))',0),100/111195),shape)
    },
    // 针对解决方案2生成代码片段
    buildCompleteCode () {
      if (!this.completeGeometry) return ''
      let type = this.completeGeometry.type
      let resultResourceString = ''
      switch (type) {
        case 'point': {
          // 点处理规则，暂无场景需求待完善
          resultResourceString = ''
          break
        }
        case 'polyline': {
          // 线处理规则，暂无场景需求待完善
          resultResourceString = ''
          break
        }
        case 'polygon': {
          resultResourceString = 'POLYGON(('
          let points = this.completeGeometry.rings[0]
          for (let i in points) {
            let point = points[i]
            let x = point[0]
            let y = point[1]
            // eslint-disable-next-line eqeqeq
            if (i == points.length - 1) {
              // 首尾点位相同
              let point0 = points[0]
              let x0 = point0[0]
              let y0 = point0[1]
              resultResourceString += x + ' ' + y + ',' + x0 + ' ' + y0 + '))'
            } else {
              resultResourceString += x + ' ' + y + ','
            }
          }
          break
        }
      }
      return resultResourceString
    },
    // 根据图像字符串渲染形状（暂时仅支持面的渲染）
    renderPolygonString (polygonString) {
      // let polygonString = 'POLYGON((117.30383035974343 39.115008652291515,117.2796263801591 39.096641093316165,117.29129922137707 39.07003388171636,117.3213396215704 39.06917558456798,117.3285493176168 39.0985293470426,117.32837765818712 39.09887266590196,117.30383035974343 39.115008652291515))'
      if (!polygonString) return console.warn(`POLYGON参数无效`)
      const polygonPathString = polygonString.substring(9, polygonString.length - 2)
      let points = polygonPathString.split(',')
      let vertices = new Array([])
      for (let i = 0; i < points.length; i++) {
        let point = points[i].trim().split(' ')
        let vertice = [Number(point[0]), Number(point[1])]
        vertices[0].push(vertice)
      }
      this.clearResourceDraw()
      let graphic = new this.gisConstructor.Graphic({
        geometry: {
          type: 'polygon',
          rings: vertices,
          spatialReference: this.view.spatialReference
        },
        symbol: {
          type: 'simple-fill',
          color: [30, 212, 230, 0.1],
          style: 'solid',
          outline: { color: [212, 202, 3], width: 2 }
        }
      })
      this.drawResourceLayer.add(graphic)
    },
    // 清除绘画结果
    clearResourceDraw () {
      this.completeGeometry = null
      this.drawResourceLayer.graphics.removeAll()
      this.bufferResourceLayer.graphics.removeAll()
    }
  }
}
